import { Checkbox } from "@/components/essentials/checkbox";
import { css } from "@emotion/react";
import { Box, CheckboxGroup, Flex, Image } from "@chakra-ui/react";
import { type GraduateServiceDocuments } from "./types";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { type FC } from "react";
import { Typography } from "@/components/essentials/typography";
import { documentCheckboxFormTestId, smilezemiGradeOptions } from "./const";
import { GraduateServiceCompanies } from "@/gql/generated/graphql.gen";
import { Select } from "@/components/essentials/select";
import { FamilyChildren } from "@/pages/quit/types";

export interface DocumentCheckboxFormProps {
  readonly documents: GraduateServiceDocuments[];
  readonly familyChildren: FamilyChildren;
  readonly checkboxField?: string;
  readonly isDisabled?: boolean;
}

export const DocumentCheckboxForm: FC<DocumentCheckboxFormProps> = ({
  documents,
  familyChildren,
  checkboxField = "documentCheckbox",
  isDisabled = false,
}) => {
  const { control, register } = useFormContext();

  const { field } = useController({
    name: checkboxField,
    control,
    // デフォルトチェックの場合は設定する
    // defaultValue: documents.map((document) => document.kind),
  });

  const selectedValues = useWatch({
    control,
    name: checkboxField,
  });

  const smilezemiIndex = documents.findIndex((document) => document.kind === GraduateServiceCompanies.Smilezemi);

  const isSelectSmilezemi =
    Array.isArray(selectedValues) && selectedValues.includes(GraduateServiceCompanies.Smilezemi);

  return (
    <CheckboxGroup {...field}>
      {documents.map((document, index) => {
        return (
          <>
            <Checkbox
              key={index}
              value={document.kind}
              data-testid={`${documentCheckboxFormTestId}-${checkboxField}${index}`}
              mt={3}
              px={2.5}
              py={3}
              w={{
                base: "100%",
                sm: "100%",
              }}
              size={{
                base: "md",
                sm: "md",
                md: "md",
                lg: "lg",
                xl: "lg",
                "2xl": "lg",
              }}
              textAlign="left"
              alignItems="flex-start"
              css={css`
                &::after {
                  position: absolute;
                  inset: 0;
                  margin: auto;
                  content: "";
                  border: solid 1px #dbdad7;
                  border-radius: 8px;
                }
                &[data-checked]::after {
                  border-color: #77bd55;
                }
                & span:last-child {
                  width: calc(100% - 1.5rem);
                }
              `}
              isDisabled={isDisabled}
            >
              <Flex width="100%" gap="12px">
                {document.image && (
                  <Box display="flex" justifyContent="center" alignItems="center">
                    <Image maxW="100px" flexBasis="100px" src={document.image} alt="" />
                  </Box>
                )}
                <Box whiteSpace="pre-wrap">
                  {document.title && <Typography variant="text2">{document.title}</Typography>}
                  {document.description && (
                    <Typography variant="text5" mt={document.title ? 1 : 0}>
                      {document.description}
                    </Typography>
                  )}
                </Box>
              </Flex>
            </Checkbox>
            <Box key="smilezemi-comment">
              {index === smilezemiIndex &&
                isSelectSmilezemi &&
                !!familyChildren &&
                (familyChildren.length === 1 ? (
                  // お子さまが一人の場合
                  <Box key={familyChildren[0]?.uuid} textAlign="center" w="100%" my={4}>
                    <Typography variant="display6" textAlign="left" mb="0.5rem">
                      お申し込みされる資料のコースを選択してください。
                    </Typography>
                    <Select
                      // data-testid={applicationChannelSelectTestId}
                      {...register(`smilezemiSelectedValues.0`)}
                    >
                      {smilezemiGradeOptions.map((option, key) => (
                        <option key={key} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </Box>
                ) : (
                  // お子さまが複数の場合
                  <Box>
                    {familyChildren.map((child, index) => (
                      <Box key={child?.uuid} textAlign="center" w="100%" my={4}>
                        <Typography variant="display6" textAlign="left" mb="0.5rem">
                          {index + 1}
                          人目のお子さまのお申し込みされる資料のコースを選択してください。
                        </Typography>
                        <Select
                          // data-testid={applicationChannelSelectTestId}
                          {...register(`smilezemiSelectedValues.${index}`)}
                        >
                          {/* 2人目以降の場合は「希望しない」を追加 */}
                          {index > 0 && (
                            <option key="none" value="希望しない">
                              希望しない
                            </option>
                          )}
                          {smilezemiGradeOptions.map((option, key) => (
                            <option key={key} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                      </Box>
                    ))}
                  </Box>
                ))}
            </Box>
          </>
        );
      })}
    </CheckboxGroup>
  );
};
